import { ref, provide, computed, watchEffect } from "vue";
import { useStore } from "vuex";
import {
  options,
  windtagDevicesColumns,
} from "@/helpers/windtagDeviceTableHelper";
import { groupBy } from "@/helpers/sharedHelper";
import $ from "jquery";

export default function useWindtagDevicesTable() {
  const store = useStore();

  const loading = computed(() => store.getters["windtagDevices/isLoading"]);
  const deviceQuery = computed(
    () => store.getters["windtagDevices/getDeviceQuery"]
  );
  const pageLength = computed(() => deviceQuery.value.length);
  const totalRecords = computed(
    () => store.getters["windtagDevices/getRecordsTotal"]
  );
  const recordsFiltered = computed(
    () => store.getters["windtagDevices/getRecordsFiltered"]
  );
  const tableData = computed(() => store.getters["windtagDevices/getDevices"]);

  let devicesFilter = deviceQuery.value;

  const fetchDevices = async () => {
    store.dispatch("windtagDevices/setDeviceQuery", devicesFilter);
    await store.dispatch("windtagDevices/fetchWindtagDevices");
    selectedDevices.value = [];
  };

  const tableOptions = {
    ...options,
    destroy: true,
    columns: windtagDevicesColumns,
    pageLength: pageLength.value,
    deferLoading: totalRecords.value,
    ajax: (data, callback) => {
      if (!loading.value) {
        const validData = Array.isArray(tableData.value) ? tableData.value : [];
        const validRecordsTotal = totalRecords.value || validData.length || 0;
        const validRecordsFiltered =
          recordsFiltered.value || validData.length || 0;

        callback({
          draw: data.draw,
          recordsTotal: validRecordsTotal,
          recordsFiltered: validRecordsFiltered,
          data: validData,
        });
      }
    },
    buttons: [
      function () {
        return {
          text: "Select All",
          action: function (_, dt) {
            dt.rows().select();
          },
          init: function (api, node) {
            $(node).attr("id", "select-all-button");

            watchEffect(() => deviceQuery.value.courses);
          },
        };
      },
      {
        text: "Deselect All",
        action: function (_, dt) {
          dt.rows().deselect();
        },
      },
      function () {
        return {
          extend: "csvHtml5",
          text: "CSV",
          title: "Central",
          exportOptions: {
            columns: [0, 1, 2, 3, 4],
          },
          customizeData: (data) => {
            const dt = this;
            const selectedRows = dt.rows({ selected: true }).data().toArray();
            if (selectedRows.length > 0) {
              data.body = selectedRows.map((row) => Object.values(row));
            } else {
              const allRows = dt.rows().data().toArray();
              data.body = allRows.map((row) => Object.values(row));
            }
          },
        };
      },
    ],
  };
  const selectedDevices = ref([]);

  const selectedDevicesByCourseName = computed(() => {
    return groupBy(selectedDevices.value, "course_name");
  });

  provide("selectedDevices", selectedDevices);

  provide("selectedDevicesByCourseName", selectedDevicesByCourseName);

  const onPageChange = ({ event }) => {
    const info = event.dt.page.info();
    devicesFilter.start = info.page * info.length;
    fetchDevices();
  };

  const handleSearch = ({ dt }) => {
    const dataTable = dt.api;
    if (dataTable && typeof dataTable.search === "function") {
      const searchValue = dataTable.search();
      if (deviceQuery.value.search !== searchValue) {
        devicesFilter.start = 0;
        devicesFilter.search = searchValue;
        fetchDevices();
      }
    }
  };

  const handleSortChange = ({ dt }) => {
    const dataTable = dt.api;
    if (dataTable && typeof dataTable.order === "function") {
      const order = dataTable.order();
      if (!order || order.length === 0) {
        if (devicesFilter.orderBy !== "id" || devicesFilter.dir !== "asc") {
          devicesFilter.orderBy = "id";
          devicesFilter.dir = "asc";
          fetchDevices();
        }
        return;
      }

      const direction = order[0][1];
      let column =
        direction === "" ? "id" : windtagDevicesColumns[order[0][0]].data;
      const sortMap = {
        course_name: "course_id",
      };

      if (sortMap[column]) {
        column = sortMap[column];
      }
      const normalizedDirection = direction === "" ? "asc" : direction;

      if (
        devicesFilter.orderBy !== column ||
        devicesFilter.dir !== normalizedDirection
      ) {
        devicesFilter.orderBy = column;
        devicesFilter.dir = normalizedDirection;

        fetchDevices();
      }
    }
  };

  const handleLengthChange = ({ dt }) => {
    const newLength = dt._iDisplayLength;
    devicesFilter.length = newLength;
    devicesFilter.start = 0;
    fetchDevices();
  };

  const onSelect = ({ dt }) => {
    const selectedRows = dt.rows({ selected: true }).data();
    selectedDevices.value = selectedRows.toArray();
  };

  const onDeselect = ({ dt }) => {
    const deSelectedRows = dt.rows({ selected: true }).data();
    selectedDevices.value = deSelectedRows.toArray();
  };

  const resetTable = () => {
    store.commit("windtagDevices/RESET_DEVICE_QUERY");
    store.commit("devicesFilterOptions/RESET_FILTER_OPTIONS");
    store.dispatch("windtagDevices/fetchWindtagDevices");
  };

  return {
    tableData,
    windtagDevicesColumns,
    tableOptions,
    onPageChange,
    handleSearch,
    handleSortChange,
    handleLengthChange,
    onSelect,
    onDeselect,
    resetTable,
  };
}
